// code-guardian-ignore-duplicates: this IS the shared master-page tab strip; the tabbed pages that need other tab semantics (role=tab/aria-selected, badges, label functions) draw the same StoreMasterPage classes inline on purpose
import type { ComponentType, ReactElement } from 'react';

export interface MasterTab<K extends string> {
  key: K;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Shown in brackets after the label. Null or left out: no number at all (not yet known). */
  count?: number | null;
  /** The tooltip, for a tab whose name needs a word more. */
  title?: string;
}

interface MasterTabStripProps<K extends string> {
  tabs: MasterTab<K>[];
  /** The key of the open tab. */
  active: K;
  onSelect: (key: K) => void;
}

/**
 * The tab strip of the master pages, as StoreMasterPage draws it: a light tray, the open
 * tab a white pill in the primary colour, an icon before each name and the count in
 * brackets after it. The same classes, kept in one place for the pages that share a strip
 * between two components (the Employees page and its roster).
 */
export default function MasterTabStrip<K extends string>({ tabs, active, onSelect }: MasterTabStripProps<K>): ReactElement {
  return (
    <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onSelect(t.key)}
            title={t.title}
            aria-pressed={isActive}
            className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
              isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Icon className="h-4 w-4" />
            {t.label}
            {typeof t.count === 'number' ? ` (${t.count.toLocaleString()})` : ''}
          </button>
        );
      })}
    </div>
  );
}
